import { useEffect, useState } from 'react';

import { api } from '../api/endpoints';
import type { Bonuses } from '../api/types';
import { formatEventDate, formatIdr } from '../format';

/** Знак у суммы значим: начисление и списание должны различаться
 *  до чтения подписи, иначе история выглядит одинаковыми строками. */
function signed(amount: number): string {
  const value = Math.abs(amount).toLocaleString('ru-RU');
  return `${amount > 0 ? '+' : '−'}${value}`;
}

export function BonusPage() {
  const [data, setData] = useState<Bonuses | null>(null);
  const [copied, setCopied] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    api.bonuses().then(setData).catch(() => setFailed(true));
  }, []);

  async function copyLink() {
    if (!data) return;
    try {
      await navigator.clipboard.writeText(data.invite_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Буфер обмена может быть недоступен — ссылка видна и так.
      setCopied(false);
    }
  }

  if (failed) {
    return (
      <div className="screen">
        <h1 className="screen__title">Бонусы</h1>
        <p className="muted">Не удалось загрузить. Попробуйте позже.</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="screen">
        <h1 className="screen__title">Бонусы</h1>
        <div className="skeleton" />
      </div>
    );
  }

  return (
    <div className="screen">
      <h1 className="screen__title">Бонусы</h1>

      <section className="balance">
        <div className="balance__sum">{formatIdr(data.balance_idr)}</div>
        <div className="balance__split">
          <span>заработано {data.total_earned_idr.toLocaleString('ru-RU')}</span>
          <span>потрачено {data.total_spent_idr.toLocaleString('ru-RU')}</span>
        </div>
      </section>

      <section className="block">
        <h2 className="block__title">Приглашайте</h2>
        <p className="muted">
          Бонус приходит, когда приглашённый оплатил участие. Потратить можно
          на любое мероприятие — вплоть до полной стоимости.
        </p>
        <div className="invite">{data.invite_url}</div>
        <button className="btn" onClick={copyLink}>
          {copied ? 'Скопировано' : 'Скопировать ссылку'}
        </button>
      </section>

      {data.invited.length > 0 && (
        <section className="block">
          <h2 className="block__title">Кого пригласили</h2>
          {data.invited.map((person, index) => (
            <div key={person.user_id ?? `pending-${index}`} className="invited">
              <div className="invited__name">
                {person.full_name ?? 'Перешёл по ссылке, ещё не заполнил анкету'}
              </div>
              <div className="invited__sum">
                {person.earned_idr > 0
                  ? `+${person.earned_idr.toLocaleString('ru-RU')}`
                  : '—'}
              </div>
            </div>
          ))}
        </section>
      )}

      {data.history.length > 0 ? (
        <section className="block">
          <h2 className="block__title">История</h2>
          {data.history.map((entry) => (
            <div key={entry.id} className="ledger">
              <div className="ledger__row">
                <span className="ledger__label">{entry.label}</span>
                <span
                  className={`ledger__sum${entry.amount_idr > 0 ? ' ledger__sum--plus' : ''}`}
                >
                  {signed(entry.amount_idr)}
                </span>
              </div>
              <div className="muted small">
                {formatEventDate(entry.created_at)}
                {entry.note && ` · ${entry.note}`}
              </div>
            </div>
          ))}
        </section>
      ) : (
        <p className="muted">
          Начислений пока нет. Первое появится, когда приглашённый вами человек
          оплатит участие.
        </p>
      )}
    </div>
  );
}
